import {
  ConceptMap,
  Consent,
  DocumentReference,
  Reference,
} from '@medplum/fhirtypes';
import type { TFunction } from 'i18next';
import {
  GENETIC_TEST_CONSENT_CODES,
  CONSENT_SCOPE,
  CONSENT_CATEGORY,
  CONSENT_PROVISION_TYPES,
  CONSENT_POLICY_RULE,
  RELATIONSHIP_CODE_MAPPINGS,
  CONSENT_STATUS_TAG_CODES,
} from '@consts/consent.consts';
import { formatDateForFHIR } from '@utils/date.utils';
import { ConsentData, ConsentRequirement } from '../types/consent.types';
import { MedplumClient } from '@medplum/core';

/**
 * Parse FHIR Consent resource into ConsentData structure
 * @param consent - The FHIR Consent resource to parse
 * @returns Parsed consent data structure
 */
export function parseConsentToData(consent: Consent): ConsentData {
  const data: ConsentData = {
    general: null,
    generalDate: null,
    scientificUse: null,
    scientificUseDate: null,
    photoVideoUse: null,
    incidentalFindings: null,
    incidentalFindingsDate: null,
    secondaryFindings: null,
    secondaryFindingsDate: null,
    // Signer information
    signerType: null,
    selectedRelatedPerson: undefined,
    newRelatedPersonName: undefined,
    newRelatedPersonRelationship: undefined,
  };

  data.general = consent.provision?.type === CONSENT_PROVISION_TYPES.PERMIT;
  data.generalDate = consent.provision?.period?.start
    ? new Date(consent.provision.period.start)
    : null;

  // Parse provisions based on codes, not text
  consent.provision?.provision?.forEach((provision) => {
    const code = provision.code?.[0]?.coding?.[0]?.code;
    const isPermit = provision.type === CONSENT_PROVISION_TYPES.PERMIT;
    const date = provision.period?.start
      ? new Date(provision.period.start)
      : null;

    switch (code) {
      case GENETIC_TEST_CONSENT_CODES.SCIENTIFIC_USE.code:
        data.scientificUse = isPermit;
        data.scientificUseDate = date;

        // Check nested provision for photo/video
        provision.provision?.forEach((nestedProvision) => {
          const nestedCode = nestedProvision.code?.[0]?.coding?.[0]?.code;
          if (nestedCode === GENETIC_TEST_CONSENT_CODES.PHOTO_VIDEO.code) {
            data.photoVideoUse =
              nestedProvision.type === CONSENT_PROVISION_TYPES.PERMIT;
            data.photoVideoUseDate = nestedProvision.period?.start
              ? new Date(nestedProvision.period.start)
              : null;
          }
        });
        break;

      case GENETIC_TEST_CONSENT_CODES.INCIDENTAL_FINDINGS.code:
        data.incidentalFindings = isPermit;
        data.incidentalFindingsDate = date;
        break;

      case GENETIC_TEST_CONSENT_CODES.SECONDARY_FINDINGS.code:
        data.secondaryFindings = isPermit;
        data.secondaryFindingsDate = date;
        break;

      default:
        // Handle undefined or unknown codes
        break;
    }
  });

  // Parse signer information from performer field
  if (consent.performer && consent.performer.length > 0) {
    const performer = consent.performer[0];
    if (performer.reference?.startsWith('Patient/')) {
      data.signerType = 'patient';
    } else if (performer.reference?.startsWith('RelatedPerson/')) {
      data.signerType = 'other';
      // The RelatedPerson will be loaded separately in the useConsent hook
    }
  }

  return data;
}

/**
 * Create FHIR Consent resource from ConsentData
 * @param consentData - The consent data to convert
 * @param patientId - The ID of the patient
 * @param serviceRequestId - The ID of the service request
 * @param serviceRequestIdentifier - The identifier of the service request
 * @param t - Translation function
 * @param documentReferences - Optional array of document reference IDs
 * @param performerReference - Optional reference to the performer (Patient or RelatedPerson)
 * @returns FHIR Consent resource
 */
export function createConsentResource(
  consentData: ConsentData,
  patientId: string,
  serviceRequestId: string,
  serviceRequestIdentifier: string,
  t: TFunction<any, any>,
  documentReferences?: string[],
  performerReference?: string
): Consent {
  const provisions: any[] = [];

  // Scientific use with nested photo/video consent
  const scientificProvision: any = {
    type: consentData.scientificUse
      ? CONSENT_PROVISION_TYPES.PERMIT
      : CONSENT_PROVISION_TYPES.DENY,
    code: [
      {
        coding: [GENETIC_TEST_CONSENT_CODES.SCIENTIFIC_USE],
        text: t('consent.sections.scientificUse.title', { ns: 'lims' }),
      },
    ],
    ...(consentData.scientificUseDate && {
      period: {
        start: formatDateForFHIR(consentData.scientificUseDate),
      },
    }),
  };

  // Add photo/video as nested provision if scientific use is permitted
  if (consentData.scientificUse) {
    scientificProvision.provision = [
      {
        type: consentData.photoVideoUse
          ? CONSENT_PROVISION_TYPES.PERMIT
          : CONSENT_PROVISION_TYPES.DENY,
        code: [
          {
            coding: [GENETIC_TEST_CONSENT_CODES.PHOTO_VIDEO],
            text: t('consent.sections.photoVideo.checkbox', { ns: 'lims' }),
          },
        ],
      },
    ];
  }

  provisions.push(scientificProvision);

  // Incidental findings
  provisions.push({
    type: consentData.incidentalFindings
      ? CONSENT_PROVISION_TYPES.PERMIT
      : CONSENT_PROVISION_TYPES.DENY,
    code: [
      {
        coding: [GENETIC_TEST_CONSENT_CODES.INCIDENTAL_FINDINGS],
        text: t('consent.sections.incidentalFindings.title', { ns: 'lims' }),
      },
    ],
    ...(consentData.incidentalFindingsDate && {
      period: {
        start: formatDateForFHIR(consentData.incidentalFindingsDate),
      },
    }),
  });

  // Secondary findings
  provisions.push({
    type: consentData.secondaryFindings
      ? CONSENT_PROVISION_TYPES.PERMIT
      : CONSENT_PROVISION_TYPES.DENY,
    code: [
      {
        coding: [GENETIC_TEST_CONSENT_CODES.SECONDARY_FINDINGS],
        text: t('consent.sections.secondaryFindings.title', { ns: 'lims' }),
      },
    ],
    ...(consentData.secondaryFindingsDate && {
      period: {
        start: formatDateForFHIR(consentData.secondaryFindingsDate),
      },
    }),
  });

  const consent: Consent = {
    resourceType: 'Consent',
    status: 'active',
    scope: {
      coding: [CONSENT_SCOPE],
    },
    category: [
      {
        coding: [CONSENT_CATEGORY],
      },
      {
        coding: [
          consentData.general
            ? CONSENT_STATUS_TAG_CODES.PERMIT
            : CONSENT_STATUS_TAG_CODES.DENY,
        ],
      },
    ],
    patient: { reference: `Patient/${patientId}` },
    dateTime: new Date().toISOString(),
    performer: performerReference
      ? [{ reference: performerReference }]
      : [{ reference: `Patient/${patientId}` }],
    ...(documentReferences &&
      documentReferences.length > 0 && {
        sourceReference: {
          reference: `DocumentReference/${documentReferences[0]}`,
        },
      }),
    provision: {
      type: consentData.general
        ? CONSENT_PROVISION_TYPES.PERMIT
        : CONSENT_PROVISION_TYPES.DENY,
      ...(consentData.generalDate && {
        period: {
          start: formatDateForFHIR(consentData.generalDate),
        },
      }),
      provision: provisions,
      data: [
        {
          meaning: 'related' as const,
          reference: {
            reference: `ServiceRequest/${serviceRequestId}`,
            display: `Genetic Test Order ${serviceRequestIdentifier}`,
          },
        },
      ],
    },
    policyRule: {
      coding: [CONSENT_POLICY_RULE],
      text: 'Common Rule Informed Consent',
    },
  };

  // Add document references if provided
  if (documentReferences && documentReferences.length > 0) {
    consent.sourceReference = {
      reference: `DocumentReference/${documentReferences[0]}`,
    };
  }

  return consent;
}

/**
 * Helper function to get relationship display text from code
 * @param code - The code to get the display text for
 * @returns The display text for the code
 */
export function getRelationshipDisplay(code: string): string {
  return (
    RELATIONSHIP_CODE_MAPPINGS[code as keyof typeof RELATIONSHIP_CODE_MAPPINGS]
      ?.display || code
  );
}

/**
 * Helper function to get translated relationship display text
 * @param displayText - The stored display text (e.g., "mother", "father")
 * @param t - Translation function
 * @returns The translated relationship text
 */
export function getTranslatedRelationshipDisplay(
  displayText: string,
  t: any
): string {
  // Map the stored display text to translation keys
  const translationKeyMap: Record<string, string> = {
    mother: 'consent.relationships.mother',
    father: 'consent.relationships.father',
    guardian: 'consent.relationships.guardian',
    'power of attorney': 'consent.relationships.powerOfAttorney',
    powerofattorney: 'consent.relationships.powerOfAttorney',
    grandparent: 'consent.relationships.grandparent',
    sibling: 'consent.relationships.sibling',
  };

  const translationKey = translationKeyMap[displayText.toLowerCase()];
  if (translationKey) {
    return t(translationKey, { ns: 'lims' });
  }

  // Fallback to original display text if no translation found
  return displayText;
}

/**
 * Attempts to resolve a FHIR Reference into a DocumentReference.
 *
 * - Safely handles missing or invalid `reference` fields.
 * - Catches and logs errors from `medplum.readReference` instead of throwing.
 * - Returns `null` if the reference cannot be resolved or is not a DocumentReference.
 *
 * @param sourceRef - A FHIR Reference (typically from Consent.sourceReference).
 * @param medplum - The Medplum client used to resolve the reference.
 * @returns A resolved DocumentReference, or null if not found or invalid.
 */
export async function loadDocumentReference(
  sourceRef: Reference | undefined,
  medplum: MedplumClient
): Promise<DocumentReference | null> {
  if (!sourceRef?.reference) {
    return null;
  }
  try {
    const docRef = await medplum.readReference(sourceRef);
    return docRef.resourceType === 'DocumentReference'
      ? (docRef as DocumentReference)
      : null;
  } catch (error) {
    console.error('Error loading linked document:', error);
    return null;
  }
}

/**
 * Utility function to get required consents for an exam type
 * @param examCode - The exam type code to get consent requirements for
 * @param conceptMap - The ConceptMap resource from the server
 * @returns Array of consent requirements for the exam type
 */
export function getRequiredConsentsForExam(
  examCode: string,
  conceptMap: ConceptMap
): ConsentRequirement[] {
  const group = conceptMap.group?.[0];

  if (!group) {
    return getDefaultConsentRequirements();
  }

  // Find the mapping for the specific exam code
  const element = group.element?.find((el) => el.code === examCode);

  if (element?.target) {
    return element.target.map((target) => ({
      code: target.code || '',
      required: target.comment === 'Required',
      comment: target.comment,
    }));
  }

  // Return default requirements if no specific mapping found
  return getDefaultConsentRequirements();
}

/**
 * Get default consent requirements for exams without specific mappings
 * @returns Array of default consent requirements
 */
function getDefaultConsentRequirements(): ConsentRequirement[] {
  return [
    { code: 'general-consent', required: true, comment: 'Required' },
    { code: 'scientific-use', required: true, comment: 'Required' },
  ];
}

/**
 * Check if a specific consent is required for an exam type
 * @param examCode - The exam type code
 * @param consentCode - The consent type code to check
 * @param conceptMap - The ConceptMap resource from the server
 * @returns True if the consent is required for the exam type
 */
export function isConsentRequiredForExam(
  examCode: string,
  consentCode: string,
  conceptMap: ConceptMap
): boolean {
  const requirements = getRequiredConsentsForExam(examCode, conceptMap);
  const requirement = requirements.find((req) => req.code === consentCode);
  return requirement?.required || false;
}

/**
 * Get all consent types that are required for an exam (all displayed consents are required)
 * @param examCode - The exam type code
 * @param conceptMap - The ConceptMap resource from the server
 * @returns Array of required consent codes
 */
export function getRequiredConsentCodes(
  examCode: string,
  conceptMap: ConceptMap
): string[] {
  const requirements = getRequiredConsentsForExam(examCode, conceptMap);
  return requirements.map((req) => req.code);
}
